import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NumericInput } from '@/components/NumericInput';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const DoorDetailDialog = ({ 
  open, 
  onOpenChange, 
  door, 
  onSave, 
  doorGlassTypes = [],
  doorMetalPanelTypes = [] 
}) => {
  const [formData, setFormData] = useState({
    doorWidth_in: null,
    doorHeight_in: null,
    roWidth_in: null,
    roHeight_in: null,
    lites: []
  });

  useEffect(() => {
    if (door && open) {
      // Initialize lites from door.lites if exists
      const lites = door.lites && door.lites.length > 0
        ? door.lites.map((lite, index) => ({
            id: index + 1,
            liteType: lite.liteType || 'glass',
            width_in: lite.width_in || null,
            height_in: lite.height_in || null,
            glassTypeId: lite.glassTypeId || '',
            metalPanelTypeId: lite.metalPanelTypeId || ''
          }))
        : [];
      
      setFormData({
        doorWidth_in: door.doorWidth_in || null,
        doorHeight_in: door.doorHeight_in || null,
        roWidth_in: door.roWidth_in || null,
        roHeight_in: door.roHeight_in || null,
        lites: lites
      });
    }
  }, [door, open]);

  const handleAddLite = () => {
    const newId = formData.lites.length > 0 
      ? Math.max(...formData.lites.map(l => l.id)) + 1 
      : 1;
    
    setFormData({
      ...formData,
      lites: [
        ...formData.lites,
        { id: newId, liteType: 'glass', width_in: null, height_in: null, glassTypeId: '', metalPanelTypeId: '' }
      ]
    });
  };

  const handleRemoveLite = (id) => {
    setFormData({
      ...formData,
      lites: formData.lites.filter(lite => lite.id !== id)
    });
  };

  const handleLiteChange = (id, field, value) => {
    const updated = formData.lites.map(lite => {
      if (lite.id === id) {
        // If changing liteType, clear the opposite type ID
        if (field === 'liteType') {
          return { 
            ...lite, 
            [field]: value,
            glassTypeId: value === 'glass' ? lite.glassTypeId : '',
            metalPanelTypeId: value === 'metalPanel' ? lite.metalPanelTypeId : ''
          };
        }
        return { ...lite, [field]: value };
      }
      return lite;
    });
    setFormData({ ...formData, lites: updated });
  };

  const blockNegativeKey = (e) => {
    if (e.key === '-') e.preventDefault();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const numericFields = [formData.doorWidth_in, formData.doorHeight_in, formData.roWidth_in, formData.roHeight_in];
    const liteFields = formData.lites.flatMap(l => [l.width_in, l.height_in]);
    if ([...numericFields, ...liteFields].some(v => v !== null && v < 0)) {
      toast.error('Value cannot be negative');
      return;
    }
    onSave(formData);
  };

  const handleCancel = () => {
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Door Details: {door?.label}</DialogTitle>
          <DialogDescription>
            Enter dimensions and lite specifications for this door
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6 mt-4">
          {/* Door Dimensions */}
          <div className="space-y-4">
            <h3 className="font-semibold text-lg border-b pb-2">Door Dimensions</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Door Width (in)</Label>
                <NumericInput
                  allowFraction
                  value={formData.doorWidth_in || ''}
                  onChange={(v) => setFormData({ ...formData, doorWidth_in: v })}
                  placeholder="e.g., 36"
                />
              </div>
              <div className="space-y-2">
                <Label>Door Height (in)</Label>
                <NumericInput
                  allowFraction
                  value={formData.doorHeight_in || ''}
                  onChange={(v) => setFormData({ ...formData, doorHeight_in: v })}
                  placeholder="e.g., 84"
                />
              </div>
              <div className="space-y-2">
                <Label>Rough Opening Width (in)</Label>
                <NumericInput
                  allowFraction
                  value={formData.roWidth_in || ''}
                  onChange={(v) => setFormData({ ...formData, roWidth_in: v })}
                  placeholder="e.g., 38"
                />
              </div>
              <div className="space-y-2">
                <Label>Rough Opening Height (in)</Label>
                <NumericInput
                  allowFraction
                  value={formData.roHeight_in || ''}
                  onChange={(v) => setFormData({ ...formData, roHeight_in: v })}
                  placeholder="e.g., 86"
                />
              </div>
            </div>
          </div>

          {/* Lites */}
          <div className="space-y-4">
            <div className="flex items-center justify-between border-b pb-2">
              <div>
                <h3 className="font-semibold text-lg">Lites</h3>
                <p className="text-xs text-slate-500 mt-1">Add glass or metal panel lites for this door</p>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleAddLite}
                className="flex items-center gap-1"
              >
                <Plus className="h-4 w-4" />
                Add Lite
              </Button>
            </div>
            
            {formData.lites.length > 0 ? (
              <div className="border rounded-lg overflow-hidden">
                {/* Table Header */}
                <div className="grid grid-cols-[100px_100px_100px_120px_1fr_40px] gap-2 bg-slate-100 px-3 py-2 text-xs font-medium text-slate-600 uppercase">
                  <div></div>
                  <div>Width (in)</div>
                  <div>Height (in)</div>
                  <div>Lite Type</div>
                  <div>Glass / Metal Panel</div>
                  <div></div>
                </div>
                
                {/* Table Body */}
                <div className="divide-y divide-slate-200">
                  {formData.lites.map((lite, index) => (
                    <div key={lite.id} className="grid grid-cols-[100px_100px_100px_120px_1fr_40px] gap-2 px-3 py-2 items-center bg-white hover:bg-slate-50">
                      <div className="text-sm font-medium text-slate-700">
                        Lite {index + 1}
                      </div>
                      <div>
                        <NumericInput
                          allowFraction
                          value={lite.width_in || ''}
                          onChange={(v) => handleLiteChange(lite.id, 'width_in', v)}
                          placeholder="0"
                          className="h-8 text-sm"
                        />
                      </div>
                      <div>
                        <NumericInput
                          allowFraction
                          value={lite.height_in || ''}
                          onChange={(v) => handleLiteChange(lite.id, 'height_in', v)}
                          placeholder="0"
                          className="h-8 text-sm"
                        />
                      </div>
                      <div>
                        <Select
                          value={lite.liteType || 'glass'}
                          onValueChange={(val) => handleLiteChange(lite.id, 'liteType', val)}
                        >
                          <SelectTrigger className="h-8 text-sm">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="glass">Glass</SelectItem>
                            <SelectItem value="metalPanel">Metal Panel</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        {lite.liteType === 'metalPanel' ? (
                          <Select
                            value={lite.metalPanelTypeId || 'none'}
                            onValueChange={(val) => handleLiteChange(lite.id, 'metalPanelTypeId', val === 'none' ? '' : val)}
                          >
                            <SelectTrigger className="h-8 text-sm">
                              <SelectValue placeholder="Select Metal Panel" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">None</SelectItem>
                              {doorMetalPanelTypes.map(mp => (
                                <SelectItem key={mp.id} value={mp.id}>{mp.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <Select
                            value={lite.glassTypeId || 'none'}
                            onValueChange={(val) => handleLiteChange(lite.id, 'glassTypeId', val === 'none' ? '' : val)}
                          >
                            <SelectTrigger className="h-8 text-sm">
                              <SelectValue placeholder="Select Glass Type" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">None</SelectItem>
                              {doorGlassTypes.map(gt => (
                                <SelectItem key={gt.id} value={gt.id}>{gt.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      </div>
                      <div className="flex justify-center">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveLite(lite.id)}
                          className="h-8 w-8 p-0 hover:bg-red-500 hover:text-white transition-colors"
                        >
                          <Trash2 className="h-4 w-4 text-red-500 hover:text-white" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-8 border rounded-lg bg-slate-50">
                <p className="text-slate-500 text-sm">No lites added yet</p>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleAddLite}
                  className="mt-3"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add First Lite
                </Button>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button type="button" variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            <Button type="submit" className="bg-[#FF6600] hover:bg-[#ff8533]">
              Save Changes
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default DoorDetailDialog;
